/**
 * SEO helpers: actualizan title/meta/OG/canonical sin duplicar tags.
 */

function ensureMetaByName (name) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  return el
}

function ensureMetaByProperty (property) {
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  return el
}

function ensureLinkRel (rel) {
  let el = document.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  return el
}

export function setDocumentTitle (title) {
  if (!title) return
  document.title = title
}

export function setMetaName (name, content) {
  if (content == null || content === '') return
  ensureMetaByName(name).setAttribute('content', String(content))
}

export function setMetaProperty (property, content) {
  if (content == null || content === '') return
  ensureMetaByProperty(property).setAttribute('content', String(content))
}

export function setCanonical (pathOrUrl) {
  if (!pathOrUrl) return
  let href = pathOrUrl
  if (pathOrUrl.startsWith('/')) {
    href = `${window.location.origin}${pathOrUrl}`
  }
  ensureLinkRel('canonical').setAttribute('href', href)
  setMetaProperty('og:url', href)
}

/** URL canónica de una tienda: siempre /tienda/:client */
export function storeCanonicalPath (clientCode) {
  if (!clientCode) return null
  return `/tienda/${encodeURIComponent(String(clientCode).trim())}`
}

/**
 * @param {object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.keywords]
 * @param {string} [opts.image]
 * @param {string} [opts.url] path o URL absoluta
 * @param {boolean} [opts.noindex]
 */
export function applyPageSeo (opts = {}) {
  const {
    title,
    description,
    keywords,
    image,
    url,
    noindex
  } = opts

  if (title) {
    setDocumentTitle(title)
    setMetaProperty('og:title', title)
    setMetaName('twitter:title', title)
  }
  if (description) {
    setMetaName('description', description)
    setMetaProperty('og:description', description)
    setMetaName('twitter:description', description)
  }
  if (keywords) {
    setMetaName('keywords', keywords)
  }
  if (image) {
    setMetaProperty('og:image', image)
    setMetaName('twitter:image', image)
  }
  if (url) {
    setCanonical(url)
  }
  if (noindex) {
    setMetaName('robots', 'noindex, nofollow')
  } else {
    setMetaName('robots', 'index, follow')
  }

  setMetaProperty('og:type', 'website')
  setMetaProperty('og:site_name', 'Click Away')
  setMetaName('twitter:card', 'summary_large_image')
}

/**
 * SEO de tienda a partir de SEO API + Name.
 * Preferir SEO.Title; fallback `${Name} - Click Away`.
 */
export function applyStoreSeo ({ seo, name, clientCode, image } = {}) {
  const s = seo || {}
  const title = (s.Title && String(s.Title).trim()) ||
    (name ? `${name} - Click Away` : 'Click Away')
  const description = (s.Description && String(s.Description).trim()) ||
    (name
      ? `Pedí online en ${name} con Click Away. Delivery y retiro.`
      : undefined)
  const keywords = (s.Keywords && String(s.Keywords).trim()) || undefined
  const path = storeCanonicalPath(clientCode)

  applyPageSeo({
    title,
    description,
    keywords,
    image,
    url: path || undefined,
    noindex: false
  })
}

export function applyProductSeo ({ productName, storeName, description, image, clientCode, productId } = {}) {
  const title = productName
    ? `${productName}${storeName ? ` | ${storeName}` : ''} - Click Away`
    : (storeName ? `${storeName} - Click Away` : 'Click Away')
  const desc = (description && String(description).trim()) ||
    (productName && storeName
      ? `${productName} en ${storeName}. Pedí online con Click Away.`
      : undefined)
  let url
  if (clientCode && productId) {
    url = `/tienda/${encodeURIComponent(clientCode)}/productos/${productId}`
  } else if (clientCode) {
    url = storeCanonicalPath(clientCode)
  }
  applyPageSeo({
    title,
    description: desc,
    image,
    url,
    noindex: false
  })
}

export default {
  applyPageSeo,
  applyStoreSeo,
  applyProductSeo,
  setCanonical,
  storeCanonicalPath,
  setDocumentTitle,
  setMetaName,
  setMetaProperty
}
